import { useCallback, useEffect, useMemo, useRef } from "react";
import { useBeforeUnload, useBlocker } from "react-router-dom";
import { useModalConfirm } from "../../../context/modal-confirm-context/ModalConfirmContext";
import {
  type NavigationGuardConfig,
  defaultNavigationGuardConfig,
} from "./useFormNavigationGuard.Parts";

export const useFormNavigationGuard = (
  hasChanges: boolean,
  config: NavigationGuardConfig = {},
) => {
  const { enabled, title, message, confirmText, cancelText } = config;
  const mergedConfig = useMemo(
    () => ({
      enabled: enabled ?? defaultNavigationGuardConfig.enabled,
      title: title ?? defaultNavigationGuardConfig.title,
      message: message ?? defaultNavigationGuardConfig.message,
      confirmText: confirmText ?? defaultNavigationGuardConfig.confirmText,
      cancelText: cancelText ?? defaultNavigationGuardConfig.cancelText,
    }),
    [enabled, title, message, confirmText, cancelText],
  );
  const shouldBlock = mergedConfig.enabled && hasChanges;
  const isShowingDialogRef = useRef(false);
  const { confirm } = useModalConfirm();

  const blockerCondition = useCallback(
    ({
      currentLocation,
      nextLocation,
    }: {
      currentLocation: { pathname: string };
      nextLocation: { pathname: string };
    }) => shouldBlock && currentLocation.pathname !== nextLocation.pathname,
    [shouldBlock],
  );

  const blocker = useBlocker(blockerCondition);

  useEffect(() => {
    if (blocker.state === "blocked" && !isShowingDialogRef.current) {
      isShowingDialogRef.current = true;

      confirm({
        title: mergedConfig.title,
        description: mergedConfig.message,
        confirmLabel: mergedConfig.cancelText,
        cancelLabel: mergedConfig.confirmText,
        dismissBehavior: "confirm",
      }).then((shouldStay) => {
        isShowingDialogRef.current = false;
        if (shouldStay) {
          blocker.reset?.();
        } else {
          blocker.proceed?.();
        }
      });
    }
  }, [blocker, confirm, mergedConfig]);

  useBeforeUnload(
    useCallback(
      (e: BeforeUnloadEvent) => {
        if (shouldBlock) {
          e.preventDefault();
        }
      },
      [shouldBlock],
    ),
  );

  return { isBlocked: blocker.state === "blocked" };
};
